import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const PublicHeaderNav = ({ cartCount = 0 }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, userProfile } = useAuth();

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleSearch = () => {
    // Search functionality implementation
    console.log('Search triggered');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-[1000] bg-card border-b border-border shadow-elevation-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => handleNavigation('/home')}>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Ticket" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">Tixful</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavigation('/home')}
              className="text-sm font-medium transition-colors hover:text-primary text-foreground"
            >
              Events
            </button>
            <button
              onClick={() => handleNavigation('/event-detail')}
              className="text-sm font-medium transition-colors hover:text-primary text-foreground"
            >
              Browse
            </button>
            {isAuthenticated && (
              <button
                onClick={() => handleNavigation('/my-tickets')}
                className="text-sm font-medium transition-colors hover:text-primary text-foreground"
              >
                My Tickets
              </button>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              iconName="Search"
              onClick={handleSearch}
              className="text-muted-foreground hover:text-foreground"
            >
              Search
            </Button>
            
            {cartCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigation('/checkout')}
                className="relative"
              >
                <Icon name="ShoppingCart" size={20} />
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              </Button>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigation('/my-tickets')}
                  iconName="Ticket"
                >
                  My Tickets
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="User"
                  onClick={() => handleNavigation('/my-account')}
                >
                  Profile
                </Button>
                {(userProfile?.role === 'admin' || userProfile?.role === 'manager') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Settings"
                    onClick={() => {
                      if (userProfile?.role === 'admin') {
                        handleNavigation('/admin-portal-dashboard');
                      } else {
                        handleNavigation('/event-manager-dashboard');
                      }
                    }}
                  >
                    {userProfile?.role === 'admin' ? 'Admin' : 'Manager'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigation('/login')}
                >
                  Sign In
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleNavigation('/register')}
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            {cartCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigation('/checkout')}
                className="relative"
              >
                <Icon name="ShoppingCart" size={20} />
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMobileMenu}
              iconName={isMobileMenuOpen ? "X" : "Menu"}
            />
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border shadow-elevation-2 z-[1100]">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => handleNavigation('/home')}
              className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors text-foreground hover:bg-muted"
            >
              Events
            </button>
            <button
              onClick={() => handleNavigation('/event-detail')}
              className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors text-foreground hover:bg-muted"
            >
              Browse
            </button>
            {isAuthenticated && (
              <button
                onClick={() => handleNavigation('/my-tickets')}
                className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors text-foreground hover:bg-muted"
              >
                My Tickets
              </button>
            )}
            
            <div className="border-t border-border pt-3 mt-3">
              <Button
                variant="ghost"
                size="sm"
                iconName="Search"
                onClick={handleSearch}
                className="w-full justify-start mb-2"
              >
                Search Events
              </Button>
              
              {isAuthenticated ? (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="User"
                    onClick={() => handleNavigation('/my-account')}
                    className="w-full justify-start"
                  >
                    My Profile
                  </Button>
                  {(userProfile?.role === 'admin' || userProfile?.role === 'manager') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Settings"
                      onClick={() => {
                        if (userProfile?.role === 'admin') {
                          handleNavigation('/admin-portal-dashboard');
                        } else {
                          handleNavigation('/event-manager-dashboard');
                        }
                      }}
                      className="w-full justify-start"
                    >
                      {userProfile?.role === 'admin' ? 'Admin Portal' : 'Manager Dashboard'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigation('/login')}
                    className="w-full justify-start"
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleNavigation('/register')}
                    className="w-full"
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeaderNav;